import React, { useRef, useState } from "react";
import { useConfirm } from "../context/ConfirmContext";
import { api, fileToBase64 } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  Wand2, Sparkles, RefreshCw, CheckCircle2, Lock, ShieldCheck, Info, Loader2, Upload, ImagePlus, X,
} from "lucide-react";
import { toast } from "sonner";

const STYLE_OPTIONS = [
  "Moderno y llamativo",
  "Vibrante y festivo",
  "Minimalista y elegante",
  "Retro/vintage",
  "Mexicano tradicional",
];

const AI_LIMIT = 4;

export default function AIAdBuilder({ business, ad, onAdSaved, onBusinessSaved, adminTargetBusinessId = null }) {
  const [tab, setTab] = useState("ai"); // "ai" | "upload"
  const [productOrService, setProductOrService] = useState("");
  const [discountText, setDiscountText] = useState("");
  const [style, setStyle] = useState(STYLE_OPTIONS[0]);
  const [colorPref, setColorPref] = useState("");
  const [extra, setExtra] = useState("");
  const [feedback, setFeedback] = useState("");
  const confirm = useConfirm();
  const [useLogo, setUseLogo] = useState(true);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [nonSquareSrc, setNonSquareSrc] = useState(null); // {b64, w, h} pending AI resize
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef(null);

  const [showAdTip, setShowAdTip] = useState(() => localStorage.getItem("ad_tip_dismissed") !== "1");
  const [localAiUsed, setLocalAiUsed] = useState(null);
  const isApproved = !!ad?.is_approved;
  const isAdmin = !!adminTargetBusinessId;
  const aiChanges = localAiUsed ?? (business?.ads_generated_this_month ?? 0);
  const aiRemaining = Math.max(0, AI_LIMIT - aiChanges);

  const endpoints = isAdmin
    ? {
        generate: `/admin/businesses/${adminTargetBusinessId}/ads/generate`,
        refine: `/admin/businesses/${adminTargetBusinessId}/ads/refine`,
        upload: `/admin/businesses/${adminTargetBusinessId}/ads/upload-image`,
        resize: `/admin/businesses/${adminTargetBusinessId}/ads/resize-to-square`,
        edit: { method: "put", url: `/admin/businesses/${adminTargetBusinessId}/ad` },
        approve: `/admin/businesses/${adminTargetBusinessId}/ads/approve`,
      }
    : {
        generate: "/ads/generate",
        refine: "/ads/refine",
        upload: "/ads/upload-image",
        resize: "/ads/resize-to-square",
        edit: { method: "put", url: "/ads/me" },
        approve: "/ads/me/approve",
      };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.put("/business/me", { logo_base64: b64 });
      onBusinessSaved && onBusinessSaved(data);
      toast.success("Logo guardado");
    } catch {
      toast.error("Error al subir logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const generate = async () => {
    if (!productOrService.trim()) {
      return toast.error("Indica el producto o servicio a anunciar");
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.generate, {
        product_or_service: productOrService,
        discount_text: discountText,
        style,
        color_preference: colorPref,
        extra_details: extra,
        use_logo: useLogo,
      });
      toast.success(isAdmin ? "¡Anuncio generado para el comercio!" : "¡Anuncio generado! Revísalo abajo.");
      setLocalAiUsed((prev) => (prev ?? aiChanges) + 1);
      onAdSaved && onAdSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar el anuncio");
    } finally {
      setBusy(false);
    }
  };

  const refine = async () => {
    if (isApproved) return;
    if (!feedback.trim()) return toast.error("Escribe qué quieres cambiar");
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.refine, { feedback, use_logo: useLogo });
      toast.success("Diseño refinado");
      setLocalAiUsed((prev) => (prev ?? aiChanges) + 1);
      onAdSaved && onAdSaved(data);
      setFeedback("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al refinar");
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 7_000_000) {
      return toast.error("Imagen muy grande. Máximo 5 MB.");
    }
    try {
      const b64 = await fileToBase64(file);
      const dims = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.width, h: img.height });
        img.onerror = () => reject(new Error("No se pudo leer la imagen."));
        img.src = b64;
      });
      if (dims.w !== dims.h) {
        // Not square — offer AI resize instead of rejecting outright.
        setUploadPreview(null);
        setNonSquareSrc({ b64, w: dims.w, h: dims.h });
        return;
      }
      if (dims.w < 800) {
        throw new Error(`Resolución mínima 800×800 px. La tuya mide ${dims.w}×${dims.h} px.`);
      }
      setNonSquareSrc(null);
      setUploadPreview(b64);
    } catch (err) {
      toast.error(err.message || "No se pudo leer el archivo");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resizeWithAI = async () => {
    if (!nonSquareSrc) return;
    if (aiRemaining === 0) {
      return toast.error("Sin cambios de IA disponibles este mes. Sube una imagen cuadrada.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.resize, { image_base64: nonSquareSrc.b64 });
      toast.success("Imagen ajustada con IA y publicada");
      setLocalAiUsed((prev) => (prev ?? (business?.ads_generated_this_month ?? 0)) + 1);
      onAdSaved && onAdSaved(data);
      setNonSquareSrc(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al ajustar la imagen");
    } finally {
      setBusy(false);
    }
  };

  const cancelResize = () => {
    setNonSquareSrc(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadImage = async () => {
    if (!uploadPreview) return toast.error("Selecciona una imagen primero");
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.upload, { image_base64: uploadPreview });
      toast.success("Imagen subida y publicada");
      onAdSaved && onAdSaved(data);
      setUploadPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setBusy(false);
    }
  };

  const saveText = async (updates) => {
    try {
      const { data } = await api[endpoints.edit.method](endpoints.edit.url, updates);
      onAdSaved && onAdSaved(data);
      toast.success("Anuncio actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const approve = async () => {
    if (!ad) return;
    const confirmMsg = isAdmin
      ? `¿Aprobar este diseño como definitivo del mes para "${business?.business_name}"?\n\nDespués de aprobar, el comercio no podrá generar otro diseño este mes. Solo podrá corregir texto/cantidades.`
      : "¿Aprobar este diseño como definitivo del mes?\n\nDespués de aprobar, ya NO podrás generar otro diseño este mes. Solo podrás corregir texto o cantidades (precio, descuento, descripción) si te equivocaste.";
    if (!await confirm(confirmMsg)) return;
    setApproving(true);
    try {
      const { data } = await api.post(endpoints.approve, { confirm: true });
      toast.success("¡Anuncio aprobado y publicado!");
      onAdSaved && onAdSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aprobar");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="space-y-4">
      <RulesBanner isApproved={isApproved} hasAd={!!ad} aiRemaining={aiRemaining} />

      <div className="grid md:grid-cols-2 gap-6">
        {/* LEFT: form */}
        <div className="bg-white border border-border rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" />
              <h3 className="font-display text-2xl tracking-tight">
                {ad ? "Tu anuncio del mes" : "Diseña tu anuncio"}
              </h3>
            </div>
            {/* Tab toggle */}
            {!isApproved && (
              <div className="flex rounded-full border border-border overflow-hidden text-xs font-medium">
                <button
                  onClick={() => setTab("ai")}
                  className={`px-3 py-1.5 transition-colors ${tab === "ai" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-zinc-50"}`}
                  data-testid="tab-ai"
                >
                  <Wand2 className="w-3 h-3 inline mr-1" />IA
                </button>
                <button
                  onClick={() => setTab("upload")}
                  className={`px-3 py-1.5 transition-colors ${tab === "upload" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-zinc-50"}`}
                  data-testid="tab-upload"
                >
                  <Upload className="w-3 h-3 inline mr-1" />Mi imagen
                </button>
              </div>
            )}
          </div>

          {!isAdmin && !business?.logo_base64 && !isApproved && (
            <LogoBanner onFile={handleLogoFile} busy={logoUploading} />
          )}

          {/* TAB: IA */}
          {(tab === "ai" || isApproved) && (
            <>
              {/* Estado 1: Sin anuncio aún */}
              {!ad && (
                <div className="space-y-4" data-testid="ai-builder-initial">
                  {showAdTip && !isAdmin && (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <Sparkles className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800 flex-1">
                        <strong>Tip para un mejor anuncio:</strong> usa pocas palabras. Escribe solo qué producto o servicio ofreces — la IA hace el resto.
                      </p>
                      <button
                        onClick={() => { setShowAdTip(false); localStorage.setItem("ad_tip_dismissed", "1"); }}
                        className="text-amber-400 hover:text-amber-600 shrink-0"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Cuéntale a la IA qué quieres anunciar. Tip: sé claro y concreto para resultados rápidos.
                  </p>
                  <Field label="¿Qué producto o servicio quieres anunciar? *">
                    <Input value={productOrService} onChange={(e) => setProductOrService(e.target.value)} placeholder="Ej. Tacos al pastor" data-testid="ai-product-input" />
                  </Field>
                  <Field label="¿Cuál es la promoción o descuento? (opcional)">
                    <Input value={discountText} onChange={(e) => setDiscountText(e.target.value)} placeholder="Ej. 2x1 los martes, 30% off, $50 OFF, Gratis" data-testid="ai-discount-input" />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Esto se imprime GIGANTE en el cartel. Sé contundente: "50% OFF", "2x1", "Gratis envío"…
                    </div>
                  </Field>
                  <Field label="Estilo del diseño">
                    <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white" data-testid="ai-style-select">
                      {STYLE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Colores favoritos (opcional)">
                    <Input value={colorPref} onChange={(e) => setColorPref(e.target.value)} placeholder="Ej. rojo y dorado" data-testid="ai-color-input" />
                  </Field>
                  <Field label="Detalles adicionales (opcional)">
                    <Textarea value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Ej. Solo de 6 a 10pm, incluye refresco, válido todo el mes" rows={2} data-testid="ai-extra-input" />
                  </Field>

                  {business?.logo_base64 && (
                    <LogoToggle value={useLogo} onChange={setUseLogo} logo={business.logo_base64} />
                  )}
                  <AiChangesBar used={aiChanges} limit={AI_LIMIT} />
                  <Button onClick={generate} disabled={busy || aiRemaining === 0} className="w-full btn-primary rounded-full py-6" data-testid="ai-generate-btn">
                    <Wand2 className="w-4 h-4 mr-2" />
                    {busy ? "Generando…" : "Generar mi anuncio"}
                  </Button>
                </div>
              )}

              {/* Estado 2: Anuncio existe pero no aprobado */}
              {ad && !isApproved && (
                <div className="space-y-4" data-testid="ai-builder-refine">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      <b>Aún no aprobado.</b> Puedes refinar el diseño con la IA o editar los textos. Cuando esté perfecto, <b>aprueba</b> abajo.
                    </span>
                  </div>

                  <Field label="Texto principal (descripción)">
                    <Input
                      defaultValue={ad.description}
                      onBlur={(e) => e.target.value !== ad.description && saveText({ description: e.target.value })}
                      data-testid="ad-edit-description"
                    />
                  </Field>
                  <Field label="Promoción / Descuento (texto)">
                    <Input
                      defaultValue={ad.discount_text}
                      onBlur={(e) => e.target.value !== ad.discount_text && saveText({ discount_text: e.target.value })}
                      data-testid="ad-edit-discount"
                    />
                  </Field>

                  <div className="pt-3 border-t border-border">
                    <Label className="text-sm">¿Algo que quieras cambiar del diseño?</Label>
                    <Textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Ej. Hazlo más vibrante, fondo amarillo, agranda el 2x1"
                      rows={3}
                      className="mt-1.5"
                      data-testid="ai-feedback-input"
                    />
                    {business?.logo_base64 && (
                      <LogoToggle value={useLogo} onChange={setUseLogo} logo={business.logo_base64} />
                    )}
                    <AiChangesBar used={aiChanges} limit={AI_LIMIT} />
                    <Button onClick={refine} disabled={busy || aiRemaining === 0} className="w-full mt-3 btn-secondary rounded-full py-5" data-testid="ai-refine-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {busy ? "Refinando…" : "Refinar diseño con IA"}
                    </Button>
                  </div>

                  <div className="pt-3 border-t border-border">
                    <Button
                      onClick={approve}
                      disabled={approving}
                      className="w-full rounded-full py-6 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="ai-approve-btn"
                    >
                      {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      {approving ? "Aprobando…" : "Aprobar diseño definitivo del mes"}
                    </Button>
                    <div className="text-[11px] text-muted-foreground mt-2 text-center">
                      Después de aprobar, ya no podrás generar ni refinar el diseño este mes.
                    </div>
                  </div>
                </div>
              )}

              {/* Estado 3: Aprobado */}
              {ad && isApproved && (
                <div className="space-y-4" data-testid="ai-builder-approved">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-xs text-emerald-900 flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      <b>Anuncio aprobado y publicado.</b> Solo puedes corregir errores de texto o cantidades. El próximo mes podrás generar uno nuevo del día 1 al 5.
                    </span>
                  </div>

                  <Field label="Texto principal (descripción)">
                    <Input
                      defaultValue={ad.description}
                      onBlur={(e) => e.target.value !== ad.description && saveText({ description: e.target.value })}
                      data-testid="ad-edit-description-approved"
                    />
                  </Field>
                  <Field label="Promoción / Descuento (texto)">
                    <Input
                      defaultValue={ad.discount_text}
                      onBlur={(e) => e.target.value !== ad.discount_text && saveText({ discount_text: e.target.value })}
                      data-testid="ad-edit-discount-approved"
                    />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      ⚠️ Esto solo cambia el texto del anuncio (lo que mostramos en la tarjeta del directorio), NO la imagen.
                    </div>
                  </Field>

                  <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-3 text-xs text-zinc-600 flex items-start gap-2">
                    <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>El diseño visual está bloqueado hasta el próximo mes. Si necesitas otra imagen, espera al día 1.</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* TAB: Subir imagen propia */}
          {tab === "upload" && !isApproved && (
            <div className="space-y-4" data-testid="upload-tab">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 text-xs text-blue-900 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Sube tu propio diseño. <b>Imagen cuadrada (1:1), mínimo 800×800 px, máx 5 MB</b> (PNG, JPEG o WebP). Puedes reemplazarla cuando quieras, sin límite. ¿No es cuadrada? Te ofrecemos ajustarla con IA.
                </span>
              </div>

              {/* Drop zone / file picker */}
              <div
                className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-[hsl(var(--primary))] transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-dropzone"
              >
                {uploadPreview ? (
                  <img src={uploadPreview} alt="preview" className="w-full aspect-square object-cover rounded-lg" />
                ) : (
                  <div className="text-muted-foreground space-y-2">
                    <ImagePlus className="w-10 h-10 mx-auto opacity-40" />
                    <div className="text-sm">Haz clic para seleccionar tu imagen</div>
                    <div className="text-xs">PNG · JPEG · WebP · cuadrada · mín 800×800 px · máx 5 MB</div>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleFileChange}
                data-testid="upload-file-input"
              />

              {/* Non-square: offer AI resize */}
              {nonSquareSrc && (
                <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3" data-testid="upload-resize-offer">
                  <div className="flex items-start gap-2 text-xs text-amber-800">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Tu imagen mide <b>{nonSquareSrc.w}×{nonSquareSrc.h}px</b> y no es cuadrada. Podemos <b>ajustarla a cuadrado (1:1) con IA</b>, extendiendo los bordes sin recortar tu contenido. Usa <b>1 de tus {AI_LIMIT} cambios de IA</b> del mes.
                    </span>
                  </div>
                  <img src={nonSquareSrc.b64} alt="original" className="w-full max-h-48 object-contain rounded-lg bg-white border border-amber-100" />
                  <div className="flex gap-2">
                    <Button
                      onClick={resizeWithAI}
                      disabled={busy || aiRemaining === 0}
                      className="flex-1 btn-primary rounded-full"
                      data-testid="upload-resize-ai-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {busy ? "Ajustando…" : "Ajustar con IA"}
                    </Button>
                    <Button
                      onClick={cancelResize}
                      variant="outline"
                      className="rounded-full"
                      data-testid="upload-resize-cancel-btn"
                    >
                      Cancelar
                    </Button>
                  </div>
                  {aiRemaining === 0 && (
                    <p className="text-[11px] text-amber-700">Sin cambios de IA este mes. Sube una imagen ya cuadrada.</p>
                  )}
                </div>
              )}

              {uploadPreview && (
                <button
                  onClick={() => { setUploadPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="text-xs text-muted-foreground underline"
                  data-testid="upload-clear-btn"
                >
                  Cambiar imagen
                </button>
              )}

              <Button
                onClick={uploadImage}
                disabled={busy || !uploadPreview}
                className="w-full btn-primary rounded-full py-6"
                data-testid="upload-submit-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {busy ? "Subiendo…" : "Publicar mi imagen"}
              </Button>

              {ad && (
                <div className="pt-3 border-t border-border">
                  <Field label="Texto principal (descripción)">
                    <Input
                      defaultValue={ad.description}
                      onBlur={(e) => e.target.value !== ad.description && saveText({ description: e.target.value })}
                      data-testid="upload-edit-description"
                    />
                  </Field>
                  <Field label="Promoción / Descuento (texto)">
                    <Input
                      defaultValue={ad.discount_text}
                      onBlur={(e) => e.target.value !== ad.discount_text && saveText({ discount_text: e.target.value })}
                      data-testid="upload-edit-discount"
                    />
                  </Field>
                  <Button
                    onClick={approve}
                    disabled={approving}
                    className="w-full mt-4 rounded-full py-6 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="upload-approve-btn"
                  >
                    {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {approving ? "Aprobando…" : "Aprobar diseño definitivo del mes"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: preview */}
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-muted-foreground">Vista previa del anuncio</div>
            {ad && (
              <div className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${isApproved ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="ad-status-badge">
                {isApproved ? "✓ Aprobado" : "Borrador"}
              </div>
            )}
          </div>
          <div className="aspect-square rounded-xl overflow-hidden bg-[#F4F4F5] border border-border relative">
            {busy ? (
              <div className="absolute inset-0 grid place-items-center bg-white/70 z-10">
                <div className="text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-[hsl(var(--primary))] border-t-transparent rounded-full mx-auto" />
                  <div className="mt-3 text-sm text-muted-foreground">
                    {tab === "upload" ? "Subiendo imagen…" : "La IA está diseñando…"}
                  </div>
                </div>
              </div>
            ) : null}
            {(ad?.image_base64 || uploadPreview) ? (
              <img
                src={uploadPreview || ad.image_base64}
                alt="anuncio"
                className="w-full h-full object-cover"
                data-testid="ad-preview-image"
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-muted-foreground text-sm p-6 text-center">
                Aquí aparecerá tu anuncio.
              </div>
            )}
            {ad?.discount_text && (
              <div className="absolute top-3 left-3 discount-stamp px-3 py-1.5 rounded text-sm font-bold uppercase">
                {ad.discount_text}
              </div>
            )}
          </div>
          {ad && (
            <div className="mt-4 text-xs text-muted-foreground">
              Actualizado: {new Date(ad.updated_at).toLocaleString("es-MX")} · Publicado en directorio CP {business?.postal_code}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AiChangesBar({ used, limit }) {
  const remaining = Math.max(0, limit - used);
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1" data-testid="ai-changes-bar">
      <div className="flex gap-1">
        {Array.from({ length: limit }).map((_, i) => (
          <div
            key={i}
            className={`w-4 h-1.5 rounded-full ${i < used ? "bg-[hsl(var(--primary))]" : "bg-zinc-200"}`}
          />
        ))}
      </div>
      <span>{remaining === 0 ? "Sin cambios IA disponibles este mes" : `${remaining} cambio${remaining !== 1 ? "s" : ""} IA restante${remaining !== 1 ? "s" : ""}`}</span>
    </div>
  );
}

function RulesBanner({ isApproved, hasAd, aiRemaining }) {
  if (isApproved) return null;
  return (
    <div className="bg-gradient-to-r from-[#F5F3FF] to-[#EDE9FE] border border-[#DDD6FE] rounded-2xl px-5 py-4 text-sm text-[#5B21B6] flex items-start gap-3" data-testid="ad-rules-banner">
      <Sparkles className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="space-y-1">
        <div className="font-semibold">Reglas del anuncio</div>
        <ul className="text-xs space-y-0.5 text-[#6D28D9]">
          <li>• Tienes hasta <b>4 cambios de IA por mes</b> (genera + refina). O sube tu propia imagen sin límite.</li>
          <li>• Cuando lo apruebes, ya no podrás cambiar el diseño este mes; solo textos/cantidades.</li>
          <li>• Sé claro con el descuento ("50% OFF", "2x1", "Gratis"): aparecerá GIGANTE en el cartel.</li>
          {!hasAd && <li>• Tip: si capturas bien el producto + la promo, el primer intento te queda perfecto.</li>}
        </ul>
      </div>
    </div>
  );
}

function LogoBanner({ onFile, busy }) {
  return (
    <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="logo-banner">
      <div className="flex items-center gap-3">
        <ImagePlus className="w-5 h-5 text-amber-600 shrink-0" />
        <div>
          <div className="text-sm font-medium text-amber-900">Sin logo</div>
          <div className="text-xs text-amber-700">Sube tu logo para que la IA lo incluya en el diseño.</div>
        </div>
      </div>
      <label className="cursor-pointer shrink-0">
        <span className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {busy ? "Subiendo…" : "Subir logo"}
        </span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid="logo-banner-input"
        />
      </label>
    </div>
  );
}

function LogoToggle({ value, onChange, logo }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5" data-testid="logo-toggle">
      <div className="flex items-center gap-2.5">
        <img src={logo} alt="logo" className="w-8 h-8 rounded-full object-cover border border-border" />
        <span className="text-sm">Usar mi logo en el diseño</span>
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid="logo-toggle-switch" />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
